import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, ArrowLeft, Loader2, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { Container, Eyebrow } from "../components/site/primitives";
import Seo from "../components/site/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const partnerTypes = ["Fintech Platform", "Bank / Lender", "Tax Software Provider", "Financial Advisory Firm", "Law Firm", "Other"];
const roles = ["Executive / Leadership", "Product / Platform", "Partnerships / Business Development", "Revenue / Growth", "Strategy / Operations", "Other"];
const sizes = ["Fewer than 50,000 clients", "50,000–250,000 clients", "250,000–1M clients", "1M+ clients"];
const objectives = ["Reduce customer dropoff or churn", "Improve transaction or loan conversions", "Add a new revenue stream", "Expand platform capabilities", "Evaluate embedded tax debt solution"];
const models = ["Integration (Embedded or Platform-Level)", "White-label / Reseller", "Co-marketing", "Referral"];

const steps = ["Contact Info", "Profile", "Goals"];

const Field = ({ label, children }) => (
  <div className="mb-5">
    <label className="block text-sm font-bold text-[#1E1E24] mb-2">{label}</label>
    {children}
  </div>
);

const inputCls =
  "w-full px-4 py-3 rounded-xl bg-white border border-slate-300 text-[#1E1E24] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0262B4] focus:border-transparent transition-all";

function Select({ value, onChange, options, placeholder, testId }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId}>
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

export default function BecomePartner() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    full_name: "", company_name: "", company_website: "", work_email: "",
    partner_type: "", role: "", company_size: "",
    primary_objective: "", partnership_models: [], company_fax: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleModel = (m) =>
    setForm((f) => ({
      ...f,
      partnership_models: f.partnership_models.includes(m)
        ? f.partnership_models.filter((x) => x !== m)
        : [...f.partnership_models, m],
    }));

  const validateStep = () => {
    if (step === 0) {
      if (!form.full_name || !form.company_name || !form.work_email) {
        toast.error("Please fill in your name, company, and work email.");
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.work_email)) {
        toast.error("Please enter a valid work email.");
        return false;
      }
    }
    if (step === 1 && (!form.partner_type || !form.role || !form.company_size)) {
      toast.error("Please complete your profile details.");
      return false;
    }
    return true;
  };

  const next = () => validateStep() && setStep((s) => Math.min(s + 1, 2));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!form.primary_objective) {
      toast.error("Please select your primary business objective.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/partners`, form);
      setDone(true);
      toast.success("Application submitted. Our team will follow up shortly.");
    } catch (e) {
      if (e?.response?.status === 429) {
        toast.error("Too many submissions from your network. Please try again in a few minutes.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Become a Community Tax Partner"
        description="Explore how embedded tax debt representation fits your platform. Start a discovery conversation with our partnerships team."
        path="/become-a-partner"
      />
      <section className="pt-16 pb-24 lg:pt-24">
      <Container>
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left intro */}
          <div className="lg:col-span-5">
            <Eyebrow>Become a Partner</Eyebrow>
            <h1 className="font-display font-extrabold text-[#1E1E24] text-4xl md:text-5xl leading-[1.08] tracking-tight">
              Explore How Tax Debt Representation Fits Into Your Platform
            </h1>
            <p className="mt-6 text-lg text-[#40424D] leading-relaxed">
              Whether you're focused on improving customer outcomes, reducing drop-off, or unlocking
              new revenue from your existing base, Community Tax gives you a proven path forward.
            </p>
            <div className="mt-8 bg-white rounded-2xl ct-shadow-card p-6">
              <h3 className="font-display font-bold text-[#1E1E24] mb-2">
                Interested in exploring whether Community Tax is the right fit?
              </h3>
              <p className="text-[#40424D] text-base leading-relaxed">
                Share a few details to help us prepare for a discovery call. A member of our
                partnerships team will follow up to discuss your goals, use cases, and partnership
                opportunities.
              </p>
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-[28px] ct-shadow-card p-8 lg:p-10" data-testid="partner-form-card">
              {done ? (
                <div className="text-center py-16" data-testid="form-success">
                  <div className="w-16 h-16 rounded-full bg-[#0262B4]/12 flex items-center justify-center mx-auto mb-6">
                    <PartyPopper className="w-8 h-8 text-[#0262B4]" />
                  </div>
                  <h2 className="font-display text-2xl font-bold text-[#1E1E24] mb-3">Thank you!</h2>
                  <p className="text-[#40424D] max-w-md mx-auto">
                    Your application has been received. A member of our partnerships team will reach
                    out to {form.work_email} shortly.
                  </p>
                </div>
              ) : (
                <>
                  {/* Honeypot — hidden from real users; bots that fill it are dropped server-side */}
                  <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
                    <label htmlFor="company_fax">Company Fax (leave blank)</label>
                    <input
                      type="text"
                      id="company_fax"
                      name="company_fax"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      value={form.company_fax}
                      onChange={(e) => set("company_fax", e.target.value)}
                    />
                  </div>
                  {/* Progress */}
                  <div className="flex items-center mb-10">
                    {steps.map((label, i) => (
                      <div key={label} className="flex items-center flex-1 last:flex-none">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                              i < step ? "bg-[#0262B4] text-white" : i === step ? "bg-[#1E1E24] text-white" : "bg-slate-100 text-slate-400"
                            }`}
                          >
                            {i < step ? <Check className="w-4 h-4" /> : i + 1}
                          </div>
                          <span className={`text-sm font-medium hidden sm:block ${i === step ? "text-[#1E1E24]" : "text-slate-400"}`}>
                            {label}
                          </span>
                        </div>
                        {i < steps.length - 1 && <div className={`flex-1 h-px mx-3 ${i < step ? "bg-[#0262B4]" : "bg-slate-200"}`} />}
                      </div>
                    ))}
                  </div>

                  <AnimatePresence mode="wait">
                    <motion.div
                      key={step}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.25 }}
                    >
                      {step === 0 && (
                        <div>
                          <Field label="Full Name">
                            <input className={inputCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Jane Doe" data-testid="input-full-name" />
                          </Field>
                          <Field label="Company Name">
                            <input className={inputCls} value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Acme Financial" data-testid="input-company-name" />
                          </Field>
                          <Field label="Company Website">
                            <input className={inputCls} value={form.company_website} onChange={(e) => set("company_website", e.target.value)} placeholder="https://acme.com" data-testid="input-company-website" />
                          </Field>
                          <Field label="Work Email">
                            <input className={inputCls} value={form.work_email} onChange={(e) => set("work_email", e.target.value)} placeholder="jane@acme.com" data-testid="input-work-email" />
                          </Field>
                        </div>
                      )}

                      {step === 1 && (
                        <div>
                          <Field label="Industry / Partner Type">
                            <Select value={form.partner_type} onChange={(v) => set("partner_type", v)} options={partnerTypes} placeholder="Select partner type" testId="select-partner-type" />
                          </Field>
                          <Field label="Your Role">
                            <Select value={form.role} onChange={(v) => set("role", v)} options={roles} placeholder="Select your role" testId="select-role" />
                          </Field>
                          <Field label="Company Size">
                            <Select value={form.company_size} onChange={(v) => set("company_size", v)} options={sizes} placeholder="Select company size" testId="select-company-size" />
                          </Field>
                        </div>
                      )}

                      {step === 2 && (
                        <div>
                          <Field label="Primary Business Objective">
                            <Select value={form.primary_objective} onChange={(v) => set("primary_objective", v)} options={objectives} placeholder="Select primary objective" testId="select-objective" />
                          </Field>
                          <Field label="Ideal Partnership Model (select all that apply)">
                            <div className="grid sm:grid-cols-2 gap-3">
                              {models.map((m) => {
                                const active = form.partnership_models.includes(m);
                                return (
                                  <button
                                    type="button"
                                    key={m}
                                    onClick={() => toggleModel(m)}
                                    data-testid={`model-${m.split(" ")[0].toLowerCase()}`}
                                    className={`text-left px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                                      active ? "border-[#0262B4] bg-[#0262B4]/8 text-[#1E1E24]" : "border-slate-300 text-[#40424D] hover:border-slate-400"
                                    }`}
                                  >
                                    <span className="flex items-center gap-2">
                                      <span className={`w-4 h-4 rounded flex items-center justify-center shrink-0 ${active ? "bg-[#0262B4]" : "border border-slate-300"}`}>
                                        {active && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                                      </span>
                                      {m}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          </Field>
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>

                  {/* Nav buttons */}
                  <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
                    {step > 0 ? (
                      <button onClick={back} className="inline-flex items-center gap-2 text-sm font-bold text-[#40424D] hover:text-[#1E1E24] transition-colors" data-testid="form-back">
                        <ArrowLeft className="w-4 h-4" /> Back
                      </button>
                    ) : <span />}

                    {step < 2 ? (
                      <button onClick={next} className="inline-flex items-center gap-2 px-7 py-3 rounded-full ct-grad-navy-btn text-white font-semibold transition-all hover:-translate-y-0.5" data-testid="form-next">
                        Continue <ArrowRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <button onClick={submit} disabled={submitting} className="inline-flex items-center gap-2 px-8 py-3 rounded-full ct-grad-navy-btn text-white font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-60" data-testid="form-submit">
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        Submit
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Container>
    </section>
    </>
  );
}
